import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { X, Lightbulb, CheckCircle, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module tips content with i18n keys
const moduleTipsContent = {
  receipts: {
    titleKey: 'module_tips.receipts.title',
    titleFallback: 'Welcome to Receipt Shoebox',
    tips: [
      { key: 'module_tips.receipts.tip_1', fallback: 'Upload receipt images or PDFs - AI extracts vendor, amount, and date automatically' },
      { key: 'module_tips.receipts.tip_2', fallback: 'Link receipts to transactions for complete audit protection' },
      { key: 'module_tips.receipts.tip_3', fallback: 'Receipts over $75 are especially important to keep for IRS audits' }
    ]
  },
  documents: {
    titleKey: 'module_tips.documents.title',
    titleFallback: 'Welcome to Document Center',
    tips: [
      { key: 'module_tips.documents.tip_1', fallback: 'Upload bank statements, invoices, and tax forms' },
      { key: 'module_tips.documents.tip_2', fallback: 'Use "Import to Ledger" to extract transactions from uploaded documents' },
      { key: 'module_tips.documents.tip_3', fallback: 'Quick OCR Preview lets you see extracted data before importing' }
    ]
  },
  transactions: {
    titleKey: 'module_tips.transactions.title',
    titleFallback: 'Welcome to General Ledger',
    tips: [
      { key: 'module_tips.transactions.tip_1', fallback: 'All your income and expenses appear here with IRS categorization' },
      { key: 'module_tips.transactions.tip_2', fallback: 'Import CSV files from your bank or accounting software' },
      { key: 'module_tips.transactions.tip_3', fallback: 'Mark transactions as "Final" when you\'ve verified them' }
    ]
  },
  deductions: {
    titleKey: 'module_tips.deductions.title',
    titleFallback: 'Welcome to Deduction Finder',
    tips: [
      { key: 'module_tips.deductions.tip_1', fallback: 'Deductions are automatically detected from your expense transactions' },
      { key: 'module_tips.deductions.tip_2', fallback: 'Add manual deductions for items not in your transactions' },
      { key: 'module_tips.deductions.tip_3', fallback: 'Track deductible percentages (e.g., 50% for business meals)' }
    ]
  },
  quarterly: {
    titleKey: 'module_tips.quarterly.title',
    titleFallback: 'Welcome to Quarterly Estimates',
    tips: [
      { key: 'module_tips.quarterly.tip_1', fallback: 'View estimated quarterly tax payments based on your income' },
      { key: 'module_tips.quarterly.tip_2', fallback: 'Set reminders before each quarterly deadline' },
      { key: 'module_tips.quarterly.tip_3', fallback: 'Payments are due April 15, June 15, September 15, January 15' }
    ]
  },
  mileage: {
    titleKey: 'module_tips.mileage.title',
    titleFallback: 'Welcome to Mileage Tracker',
    tips: [
      { key: 'module_tips.mileage.tip_1', fallback: 'Log business trips to claim the IRS mileage deduction' },
      { key: 'module_tips.mileage.tip_2', fallback: 'Current IRS rate is 70 cents per mile (2025)' },
      { key: 'module_tips.mileage.tip_3', fallback: 'Keep a record of business purpose for each trip' }
    ]
  },
  contractors: {
    titleKey: 'module_tips.contractors.title',
    titleFallback: 'Welcome to 1099 Contractors',
    tips: [
      { key: 'module_tips.contractors.tip_1', fallback: 'Track payments to freelancers and contractors' },
      { key: 'module_tips.contractors.tip_2', fallback: 'Contractors paid $600+ require a 1099-NEC form' },
      { key: 'module_tips.contractors.tip_3', fallback: 'Record contractor details for year-end tax filings' }
    ]
  },
  reports: {
    titleKey: 'module_tips.reports.title',
    titleFallback: 'Welcome to Reports',
    tips: [
      { key: 'module_tips.reports.tip_1', fallback: 'Generate Schedule C preview with IRS line items' },
      { key: 'module_tips.reports.tip_2', fallback: 'Download tax package PDF to share with your CPA' },
      { key: 'module_tips.reports.tip_3', fallback: 'Year-over-year comparison helps identify trends' }
    ]
  },
  strategy: {
    titleKey: 'module_tips.strategy.title',
    titleFallback: 'Welcome to Tax Strategy',
    tips: [
      { key: 'module_tips.strategy.tip_1', fallback: 'Get personalized tax-saving recommendations' },
      { key: 'module_tips.strategy.tip_2', fallback: 'View upcoming tax deadlines in the calendar' },
      { key: 'module_tips.strategy.tip_3', fallback: 'AI insights analyze your specific situation' }
    ]
  },
  businesses: {
    titleKey: 'module_tips.businesses.title',
    titleFallback: 'Welcome to Business Manager',
    tips: [
      { key: 'module_tips.businesses.tip_1', fallback: 'Manage multiple businesses from one account' },
      { key: 'module_tips.businesses.tip_2', fallback: 'Each business tracks its own income, expenses, and deductions' },
      { key: 'module_tips.businesses.tip_3', fallback: 'Add EIN and business details for accurate reporting' }
    ]
  },
  chat: {
    titleKey: 'module_tips.chat.title',
    titleFallback: 'Welcome to AI Advisors',
    tips: [
      { key: 'module_tips.chat.tip_1', fallback: 'Ask questions about deductions, tax strategies, or your specific situation' },
      { key: 'module_tips.chat.tip_2', fallback: 'Choose from 4 advisor personas with different specialties' },
      { key: 'module_tips.chat.tip_3', fallback: 'Conversation history is saved for reference' }
    ]
  },
  'bank-connections': {
    titleKey: 'module_tips.bank_connections.title',
    titleFallback: 'Welcome to Bank Connections',
    tips: [
      { key: 'module_tips.bank_connections.tip_1', fallback: 'Connect your bank to automatically import transactions' },
      { key: 'module_tips.bank_connections.tip_2', fallback: 'Review imported transactions before adding to your ledger' },
      { key: 'module_tips.bank_connections.tip_3', fallback: 'Sync regularly to keep your data up-to-date' }
    ]
  }
};

const ModuleTips = ({ moduleId }) => {
  const { t } = useTranslation();
  const { user, getAuthHeaders } = useAuth();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const config = moduleTipsContent[moduleId];

  useEffect(() => {
    if (!config || !user?.user_id) {
      setLoading(false);
      return;
    }
    checkShouldShowTips();
  }, [moduleId, user?.user_id]);

  const checkShouldShowTips = async () => {
    try {
      const response = await axios.get(`${API}/user/module-tips/${moduleId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setVisible(response.data.show_tips);
    } catch (error) {
      // If API fails, don't show tips (fail silently)
      setVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    setVisible(false);
    try {
      await axios.post(`${API}/user/module-tips/${moduleId}/dismiss`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
    } catch (error) {
      // Silently fail
    }
  };

  const handleDisableAll = async () => {
    if (window.confirm(t('module_tips.disable_confirm', 'Disable tips for all modules? You can re-enable them in Settings.'))) {
      setVisible(false);
      try {
        await axios.post(`${API}/user/module-tips/disable-all`, {}, {
          headers: getAuthHeaders(),
          withCredentials: true
        });
      } catch (error) {
        // Silently fail
      }
    }
  };

  // Don't render if loading, not visible, or no config
  if (loading || !visible || !config) return null;

  return (
    <Card 
      className={`fixed ${moduleId === 'chat' ? 'top-48' : 'bottom-4'} right-4 w-80 shadow-2xl border border-emerald-200 z-50 animate-slide-up bg-white`}
      data-testid={`module-tips-${moduleId}`}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-white" />
            </div>
            <h3 className="font-semibold text-stone-900 text-sm">
              {t(config.titleKey, config.titleFallback)}
            </h3>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleDismiss}
            className="h-8 w-8 p-0 text-stone-400 hover:text-stone-600"
            data-testid="dismiss-tips-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Tips List */}
        <ul className="space-y-2.5 mb-4">
          {config.tips.map((tip, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm">
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle className="w-3 h-3 text-emerald-600" />
              </div>
              <span className="text-stone-600 leading-relaxed">
                {t(tip.key, tip.fallback)}
              </span>
            </li>
          ))}
        </ul>
        
        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-stone-100">
          <Button 
            variant="default" 
            size="sm" 
            onClick={handleDismiss}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="got-it-btn"
          >
            <Sparkles className="w-3 h-3 mr-1.5" />
            {t('common.got_it', 'Got it!')}
          </Button>
          <button 
            className="text-xs text-stone-400 hover:text-stone-600 transition-colors"
            onClick={handleDisableAll}
            data-testid="disable-all-tips-btn"
          >
            {t('module_tips.dont_show', "Don't show tips")}
          </button>
        </div>
      </div>
    </Card>
  );
};

export default ModuleTips;
